import React from 'react';
import { Clock, Globe, Database } from 'lucide-react';
import { getCountryDisplayName } from '@/lib/countries';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';
import AddToLibraryButton from '@/components/universal-storage/AddToLibraryButton';
import ResearchAddToProjectButton from '@/components/private-research-generator/AddToProjectButton';
import { PublicResearchAddToProjectButton } from '@/components/research-generator';

interface ResearchCardFooterProps {
  researchId: string;
  locationDisplay: string | null;
  sourceTypeDisplay: string | null;
  timeframeDisplay: string | null;
  formattedDate: string;
  isFavorited: boolean;
  favoriteCount: number;
  onToggleFavorite: (researchId: string) => Promise<void>;
  showFavoriteButton?: boolean; // Optional prop to hide favorite button
  contentType?: 'research' | 'private_research'; // NEW: Optional prop for content type
  hideStorageButton?: boolean; // NEW: Optional prop to hide storage button
}

const ResearchCardFooter: React.FC<ResearchCardFooterProps> = ({ 
  researchId, 
  locationDisplay, 
  sourceTypeDisplay, 
  timeframeDisplay, 
  formattedDate,
  isFavorited,
  favoriteCount,
  onToggleFavorite,
  showFavoriteButton = true, // Default to true for backward compatibility
  contentType = 'research', // Default to 'research' for backward compatibility
  hideStorageButton = false // Default to false for backward compatibility
}) => {
  return (
    <div className="mt-auto pt-2 space-y-2">


      
      {/* Bottom section with date on left and action buttons on right */}
      <div className="flex items-end justify-between mt-auto">
        {/* Date on the left side */}
        <div className="flex items-center gap-2 mt-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-violet-500"></div>
            <span className="text-gray-600 dark:text-gray-400">{formattedDate}</span>
          </div>
        </div>
        
        {/* Action buttons on the right side */}
        <div className="flex items-center gap-2 mt-2">
          {/* Favorites button - only show if enabled */}
          {showFavoriteButton && (
            <PersistentFavoriteButton 
              itemId={researchId}
              onToggleFavorite={onToggleFavorite}
              isFavorite_prop={isFavorited}
              favoriteCount_prop={favoriteCount}
              className="p-0"
            />
          )}
          
          {/* Add to Project button - show for both public and private research */}
          {contentType === 'private_research' && (
            <ResearchAddToProjectButton
              itemId={researchId}
              dropdownLabel="Add to Project"
              variant="auto"
            />
          )}
          
          {contentType === 'research' && (
            <PublicResearchAddToProjectButton
              itemId={researchId}
              dropdownLabel="Add to Project"
              variant="auto"
            />
          )}
          
          {/* Storage button next to project button */}
          {!hideStorageButton && (
            <AddToLibraryButton
              itemId={researchId}
              contentType={contentType}
              dropdownLabel="Add to Storage"
              variant="auto"
            />
          )}
          
          {/* Source type next to storage button */}
          {sourceTypeDisplay && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-gradient-to-r from-green-50 to-emerald-100 dark:from-green-950/40 dark:to-emerald-900/30 border border-green-200/60 dark:border-emerald-700/30 rounded-full text-green-700 dark:text-emerald-300 backdrop-blur-sm">
              <Database size={12} className="text-green-500 dark:text-emerald-400" />
              <span>
                {sourceTypeDisplay.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResearchCardFooter;
